// ** React Imports
import { createContext, useRef, useState, useCallback } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Config
import { generateSegmentQueryUrl, runpodHealthUrl, segmentsListUrl } from 'src/configs/api'
import { fetchAudienceTags } from 'src/utils/audienceTags'

import {
  CUSTOM_SEGMENT_TEMPLATE,
  buildQueryBuilderFields,
  fromApiSegment,
  fromICQuery,
  syncNoValueOperatorsFromRegistry,
  syncValueInputsFromRegistry,
  toApiSegmentPayload
} from '../views/apps/audience/segments/queryPages/segmentJsonAdapter'

import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'
// ** Defaults
// A segment 400 names the offending field: 'field "age" must be at least 0, got -5' or
// 'field "gender" accepts male, female, other, got Male'. Returns { field, message } so the
// message can be shown on that rule (field is null when the message names none).
export const getSegmentRuleErrorFromResponse = error => {
  const data = error?.response?.data
  const message = data?.detail || data?.message || data?.error
  if (error?.response?.status !== 400 || typeof message !== 'string') {
    return null
  }
  const match = message.match(/field "([^"]+)"/)

  return { field: match ? match[1] : null, message }
}

// Segment listing column field -> GET /segments sort_by. "Count as of" sorts by last_run;
// the API puts never-counted segments last in both directions, so the UI doesn't reorder them.
const SEGMENT_SORT_BY = {
  name: 'name',
  type: 'type',
  status: 'status',
  created_at: 'created_at',
  updated_at: 'updated_at',
  audience_count: 'audience_count',
  last_run: 'last_run'
}

// GET /segments?type= accepts static | dynamic.
const SEGMENT_TYPE_FILTER = {
  key: 'type',
  label: 'Type',
  type: 'single',
  options: [
    { label: 'Static', value: 'static' },
    { label: 'Dynamic', value: 'dynamic' }
  ]
}

const defaultProvider = {
  tagListData: [],
  setTagListData: () => null,

  segmentListData: [],
  setSegmentListData: () => null,
  segmentName: '',
  setSegmentName: () => null,
  segmentQueryPrompt: '',
  setSegmentQueryPrompt: () => null,
  segmentQuerySql: '',
  setSegmentQuerySql: () => null,
  segmentQueryJson: null,
  isResponsePending: false,
  setIsResponsePending: () => null,
  setSegmentQueryJson: () => null,
  selectedSegment: {},
  setSelectedSegment: () => null,
  selectedSegmentID: '',
  setSelectedSegmentID: () => null,
  queryData: '',
  setQueryData: () => null,
  segmentSelectionData: [],
  setSegmentSelectionData: () => null,
  predefinedSegmentID: '',
  setPredefinedSegmentID: () => null,
  executeQueryData: '',
  setExecuteQueryData: () => null,
  segmentType: 'dynamic',
  setSegmentType: () => null,
  totalSegments: 0,
  setTotalSegments: () => null,
  tags: [],
  setTags: () => null,
  segmentFilterList: [],
  setSegmentFilterList: () => null,
  segmentOtherFilterList: [],
  setSegmentOtherFilterList: () => null,
  hasTagFilter: false,
  setHasTagFilter: () => null,
  isExecuting: false,
  setIsExecuting: () => null,
  segmentRegistryFields: [],
  getSegmentRegistry: () => Promise.resolve(),
  segmentRuleError: null,
  setSegmentRuleError: () => null,
  runpodHealth: null,
  setRunpodHealth: () => null,
  isCheckingRunpodHealth: false,
  setIsCheckingRunpodHealth: () => null,
  runpodHealthError: null,
  setRunpodHealthError: () => null,
  checkRunpodHealth: () => null
}

const SegmentContext = createContext(defaultProvider)

const SegmentProvider = ({ children }) => {
  const [tagListData, setTagListData] = useState(defaultProvider.tagListData)

  const [segmentListData, setSegmentListData] = useState(defaultProvider.segmentListData)

  const [segmentSelectionData, setSegmentSelectionData] = useState(defaultProvider.segmentSelectionData)

  const [segmentName, setSegmentName] = useState(defaultProvider.segmentName)

  const [segmentQueryPrompt, setSegmentQueryPrompt] = useState(defaultProvider.segmentQueryPrompt)

  const [segmentQuerySql, setSegmentQuerySqlState] = useState(defaultProvider.segmentQuerySql)
  const setSegmentQuerySql = useCallback(val => {
    setSegmentQuerySqlState(prev => {
      if (prev === val) {
        return prev
      }
      setExecuteQueryData('')
      return val
    })
  }, [])

  const [segmentQueryJson, setSegmentQueryJsonState] = useState(defaultProvider.segmentQueryJson)
  const setSegmentQueryJson = useCallback(val => {
    setSegmentQueryJsonState(prev => {
      if (JSON.stringify(prev) === JSON.stringify(val)) {
        return prev
      }
      setExecuteQueryData('')
      return val
    })
  }, [])
  const segmentQueryJsonRef = useRef(segmentQueryJson)
  segmentQueryJsonRef.current = segmentQueryJson

  // Cursor cache for getSegmentList's cursor-based pagination (see below).
  const segmentListCursorsRef = useRef({})
  const segmentListCursorFingerprintRef = useRef('')

  const [isResponsePending, setIsResponsePending] = useState(defaultProvider.isResponsePending)

  const [selectedSegment, setSelectedSegment] = useState(defaultProvider.selectedSegment)

  const [selectedSegmentID, setSelectedSegmentID] = useState(defaultProvider.selectedSegmentID)

  const [queryData, setQueryData] = useState(defaultProvider.queryData)

  const [predefinedSegmentID, setPredefinedSegmentID] = useState(defaultProvider.predefinedSegmentID)

  const [executeQueryData, setExecuteQueryData] = useState(defaultProvider.executeQueryData)

  const [segmentType, setSegmentType] = useState(defaultProvider.segmentType)

  const [totalSegments, setTotalSegments] = useState(defaultProvider.totalSegments)

  const [tags, setTags] = useState(defaultProvider.tags)
  // { field, message } from a 400 on execute/save, shown under the rule for that field.
  const [segmentRuleError, setSegmentRuleError] = useState(defaultProvider.segmentRuleError)
  const [segmentFilterList, setSegmentFilterList] = useState(defaultProvider.segmentFilterList)
  const [segmentOtherFilterList, setSegmentOtherFilterList] = useState(defaultProvider.segmentOtherFilterList)
  const [hasTagFilter, setHasTagFilter] = useState(defaultProvider.hasTagFilter)
  const [isExecuting, setIsExecuting] = useState(defaultProvider.isExecuting)

  const [segmentRegistryFields, setSegmentRegistryFields] = useState([])
  const segmentRegistryLoadedRef = useRef(false)

  const { showError, showSuccess } = useToast()
  // ** Hooks
  const router = useRouter()
  const api = useAPI()
  const apiRef = useRef(api)
  apiRef.current = api
  const isCheckingRunpodHealthRef = useRef(false)

  const [runpodHealth, setRunpodHealth] = useState(defaultProvider.runpodHealth)
  const [isCheckingRunpodHealth, setIsCheckingRunpodHealth] = useState(defaultProvider.isCheckingRunpodHealth)
  const [runpodHealthError, setRunpodHealthError] = useState(defaultProvider.runpodHealthError)

  // Field/operator catalogue for the query builder, sourced from GET /segments/registry
  // instead of the old hardcoded queryPages/field.js list. Fetched once and cached -
  // called from each query-builder screen's own mount effect, NOT from here. This
  // provider wraps the whole app (including the login page, in _app.js) - firing an
  // authenticated call unconditionally on mount 401s before login, which retriggers a
  // refresh, which also fails, which calls logout()'s unconditional redirect to
  // /login even when already there, reloading the page and remounting this provider -
  // an infinite refresh/registry/redirect loop.
  const getSegmentRegistry = async () => {
    if (segmentRegistryLoadedRef.current) {
      return
    }
    segmentRegistryLoadedRef.current = true

    try {
      const response = await api.get(`${segmentsListUrl}/registry`)
      setSegmentRegistryFields(buildQueryBuilderFields(response?.data))
      syncNoValueOperatorsFromRegistry(response?.data)
      syncValueInputsFromRegistry(response?.data)
    } catch {
      segmentRegistryLoadedRef.current = false
    }
  }

  const getSegmentNameById = segmentId => {
    const segment = (segmentListData || []).find(
      s => String(s?.segment_id || s?.segmentId || s?.id) === String(segmentId)
    )
    return segment?.name || segment?.segmentName || String(segmentId)
  }

  const getTag = async (search = '') => {
    try {
      const tagNames = await fetchAudienceTags(api, search)
      setTagListData(tagNames.map(tag => ({ label: tag, value: tag })))
    } catch {
      setTagListData([])
    }
  }

  const executeQuery = async () => {
    try {
      setIsExecuting(true)
      setSegmentRuleError(null)

      const inputData = {
        // The builder keeps a per-row AND/OR; the API takes one combinator per group.
        query_json: fromICQuery(segmentQueryJsonRef.current)
      }

      // The global toast only knows message/error, not CDP's `detail`, so show our own.
      const response = await api.post(`${segmentsListUrl}/execute`, inputData, { skipGlobalToast: true })

      const executeQuery_data = response?.data?.data

      setExecuteQueryData(executeQuery_data)
    } catch (error) {
      const ruleError = getSegmentRuleErrorFromResponse(error)
      setSegmentRuleError(ruleError)
      showError(ruleError?.message || 'Failed to execute segment query. Please try again.')
      return
    } finally {
      setIsExecuting(false)
    }
  }

  const getSegmentList = async (
    value,
    sortModel = [{ field: '', sort: '' }],
    page,
    pageSize,
    type = [],
    segmentTags = []
  ) => {
    try {
      const search = typeof value === 'string' ? value : ''
      const typeFilter = Array.isArray(type) ? type[0] : type
      const tagsFilter = Array.isArray(segmentTags) ? segmentTags : []
      // Grid column field -> sort_by; omitted means the API default (created_at desc).
      const sortField = SEGMENT_SORT_BY[sortModel?.[0]?.field]
      const sortOrder = sortField && ['asc', 'desc'].includes(sortModel[0].sort) ? sortModel[0].sort : undefined

      // The list endpoint pages by cursor - cache each page's cursor as it's
      // visited so Next/Previous reuse it. A page jumped to without a cursor is
      // requested by page number instead.
      const fingerprint = JSON.stringify({ search, typeFilter, tagsFilter, pageSize, sortField, sortOrder })
      if (fingerprint !== segmentListCursorFingerprintRef.current) {
        segmentListCursorFingerprintRef.current = fingerprint
        segmentListCursorsRef.current = {}
      }

      const cursors = segmentListCursorsRef.current
      const pageIndex = Math.max((page || 1) - 1, 0)
      const cursor = cursors[pageIndex]

      const params = { kind: 'custom' }
      if (search) {
        params.search = search
      }
      if (typeFilter) {
        params.type = typeFilter
      }
      if (tagsFilter.length) {
        params.tags = tagsFilter.join(',')
      }
      if (sortField) {
        params.sort_by = sortField
        params.sort_order = sortOrder || 'desc'
      }
      if (cursor) {
        params.cursor = cursor
      } else if (pageIndex > 0) {
        params.page = pageIndex + 1
      }
      if (pageSize) {
        params.limit = pageSize
      }

      const response = await api.get(segmentsListUrl, { params })
      const segments = (response?.data?.segments || []).map(fromApiSegment)
      const nextCursor = response?.data?.next_cursor

      cursors[pageIndex + 1] = nextCursor

      setSegmentListData(segments)
      setTotalSegments(response?.data?.total_count || 0)
      //     if (selectedSegment || segmentName) {
      //   setSelectedSegment('')
      //   setSegmentName('')
      // }
      // if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson) {
      //   setPredefinedSegmentID('')
      //   setSegmentName('')
      //   setSegmentQuerySql('')
      //   setSegmentQueryJson('')
      //   setTags([])
      // }
      // State clearing removed to prevent side effects during list refreshes
    } catch {
      // console.error('Error fetching segment list:', error)
      // const errorMessage = error.response?.data?.message || t('An unexpected error occurred')
      // showError(errorMessage)
      // // If the error is related to authentication, log out the user
      // if (error.response?.status === 401) {
      //   logout()
      // }
    }
  }

  const cloneSegment = async (id, segmentCloneName) => {
    try {
      const inputData = {
        name: segmentCloneName
      }
      const response = await api.post(`${segmentsListUrl}/${id}/clone`, inputData)

      if (response?.status === 200 || response?.status === 201) {
        showSuccess('Segment cloned successfully')
      }
    } catch {
      // error toast is shown by the api client's response interceptor
    }
  }

  const getSegmentSelectlist = async () => {
    const response = await api.get(segmentsListUrl, { params: { kind: 'predefined' } })
    const predefinedSegmentList = (response?.data?.segments || []).map(fromApiSegment)

    setSegmentSelectionData([CUSTOM_SEGMENT_TEMPLATE, ...predefinedSegmentList])
  }

  const createSegment = async () => {
    try {
      const inputData = toApiSegmentPayload({
        name: segmentName,
        type: segmentType,
        description: null,
        prompt: segmentQueryPrompt,
        segmentQueryJson,
        applicableChannels: [],
        tags
      })

      const response = await api.post(segmentsListUrl, inputData)

      if (response?.status === 200 || response?.status === 201) {
        showSuccess('Segment created successfully')
        if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson || tags) {
          setPredefinedSegmentID('')
          setSegmentName('')
          setSegmentQuerySql('')
          setSegmentQueryJson('')
          setTags([])
        }
        router.push('/customer-data-platform/segment')
        resetSegmentState()
      }
    } catch (error) {
      // error toast is shown by the api client's response interceptor
      setSegmentRuleError(getSegmentRuleErrorFromResponse(error))
    }
  }

  const getSegmentWithCampaigns = async id => {
    try {
      const response = await api.get(`${segmentsListUrl}/${id}`, { params: { includeCampaigns: true } })
      return fromApiSegment(response?.data) || null
    } catch {
      return null
    }
  }

  const getSegmentData = async id => {
    setSelectedSegmentID(id)

    const response = await api.get(`${segmentsListUrl}/${id}`)
    const segment = fromApiSegment(response?.data)
    setSelectedSegment(segment)
    setSelectedSegmentID(id)
    setSegmentType(segment?.type)
    setPredefinedSegmentID(segment?.predefined_segment_id)
  }

  /**
   * Generates a segment query using the provided prompt by calling the backend API.
   * @param {String} prompt: The prompt to generate the segment query.
   * @param {Object} [options] { signal } - an AbortSignal that cancels the request.
   * @returns {Object|null} The generated segment query or null if an error occurs.
   */
  const generateSegmentQuery = async (prompt, { signal } = {}) => {
    try {
      const response = signal
        ? await api.post(generateSegmentQueryUrl, { prompt }, { signal })
        : await api.post(generateSegmentQueryUrl, { prompt })

      if (response?.data?.data?.query) {
        const generatedQuery = response?.data?.data
        return generatedQuery
      } else {
        return null
      }
    } catch {
      return null
    }
  }

  /**
   * Checks the health of RunPod Serverless AI endpoints (Segment Query Builder).
   * @returns {Promise<object>} The RunPod health response data.
   */
  const checkRunpodHealth = useCallback(async () => {
    if (isCheckingRunpodHealthRef.current) {
      return
    }
    isCheckingRunpodHealthRef.current = true
    setIsCheckingRunpodHealth(true)
    try {
      const response = await apiRef.current.get(runpodHealthUrl, {
        skipGlobalToast: true,
        headers: { Accept: 'application/json' }
      })
      const data = response?.data?.data || response?.data || null
      setRunpodHealth(data)
      setRunpodHealthError(null)
      return data
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to connect to AI health check service'
      const errorPayload = {
        status: 'DOWN',
        error: errorMsg,
        endpoints: {
          segment_query_builder: {
            endpoint_id: null,
            status: 'DOWN',
            error: errorMsg
          }
        }
      }
      setRunpodHealth(errorPayload)
      setRunpodHealthError(errorMsg)
      return errorPayload
    } finally {
      isCheckingRunpodHealthRef.current = false
      setIsCheckingRunpodHealth(false)
    }
  }, [])

  const getSegmentFilterList = async () => {
    try {
      const response = await api.get(`${segmentsListUrl}/tags`)
      const data = response?.data

      let tagOptions
      let otherFilters
      if (Array.isArray(data?.tags)) {
        // CDP shape, same as /audiences/tags: { tags: ['vip', ...] } (or { name } objects).
        // type (static | dynamic) is a fixed list filter, so its options are defined here.
        tagOptions = data.tags.map(tag => (typeof tag === 'string' ? tag : tag?.name)).filter(Boolean)
        otherFilters = [SEGMENT_TYPE_FILTER]
      } else {
        // Legacy shape: { data: { filters: [{ key: 'tags', options }], otherFilters } }
        const tagsFilter = (data?.data?.filters || []).find(f => f.key === 'tags')
        tagOptions = tagsFilter ? tagsFilter.options.map(o => o.value) : null
        otherFilters = data?.data?.otherFilters || []
      }

      setHasTagFilter(tagOptions !== null)
      setSegmentFilterList(tagOptions || [])

      setSegmentOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      setSegmentFilterList([])
      setSegmentOtherFilterList([])
      return {}
    }
  }

  const editSegment = async id => {
    try {
      const inputData = toApiSegmentPayload({
        name: segmentName,
        type: segmentType,
        description: selectedSegment?.description,
        prompt: segmentQueryPrompt,
        segmentQueryJson,
        applicableChannels: selectedSegment?.applicable_channels,
        tags
      })

      // Determine the segment id to edit. Prefer the explicit selectedSegmentID state,
      // then common id fields on selectedSegment, then the passed-in `id` parameter.
      const targetId =
        selectedSegmentID || selectedSegment?.segment_id || selectedSegment?.segmentId || selectedSegment?.id || id

      await api.put(`${segmentsListUrl}/${targetId}`, inputData)
      showSuccess('Segment edited successfully')

      if (predefinedSegmentID || segmentName || segmentQuerySql || segmentQueryJson || tags) {
        setPredefinedSegmentID('')
        setSegmentName('')
        setSegmentQueryPrompt('')
        setSegmentQuerySql('')
        setSegmentQueryJson('')
        setTags([])
      }
      router.push('/customer-data-platform/segment')
    } catch (error) {
      // error toast is shown by the api client's response interceptor
      setSegmentRuleError(getSegmentRuleErrorFromResponse(error))
    }
  }

  const refreshSegment = async id => {
    try {
      await api.post(`${segmentsListUrl}/${id}/refresh`)
      showSuccess('Segment refreshed successfully')
      return true
    } catch {
      // error toast is shown by the api client's response interceptor
    }
  }

  // Single-segment usage check backed by GET /segments/:id/usage (the new spec's
  // replacement for the old bulk check-usage call). Only used by the single-delete
  // flow - shaped to match checkSegmentUsage's {safeToDelete, inUse, hasInUse}
  // return contract so the delete dialog doesn't need to change.
  const getSegmentUsage = async id => {
    const segmentId = String(id)

    try {
      const response = await api.get(`${segmentsListUrl}/${segmentId}/usage`)
      const usage = response?.data?.usage || []
      const hasInUse = !!response?.data?.in_use

      if (!hasInUse) {
        return {
          safeToDelete: [{ segmentId, segmentName: getSegmentNameById(segmentId) }],
          inUse: [],
          hasInUse: false
        }
      }

      return {
        safeToDelete: [],
        inUse: [
          {
            segmentId,
            segmentName: getSegmentNameById(segmentId),
            campaignNames: usage.map(u => u.ref_id),
            campaignCount: usage.length
          }
        ],
        hasInUse: true
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error'
      showError(`Failed to check segment usage: ${errorMsg}`)
      // Fail closed - don't allow deletion if usage can't be verified
      return { safeToDelete: [], inUse: [], hasInUse: true }
    }
  }

  // Bulk usage check backed by POST /segments/usage (up to 100 ids in one call).
  // Shaped to the same {safeToDelete, inUse, hasInUse} contract the bulk-delete UI
  // already reads, so nothing in the list page needs to change.
  const checkSegmentUsage = async selectedIDList => {
    const validSegmentIds = (selectedIDList || []).map(id => String(id))

    if (validSegmentIds.length === 0) {
      return { safeToDelete: [], inUse: [], hasInUse: false }
    }

    try {
      const response = await api.post(`${segmentsListUrl}/usage`, { segment_ids: validSegmentIds })

      const safeToDelete = (response?.data?.segments || [])
        .filter(s => !s.in_use)
        .map(s => ({
          segmentId: s.segment_id,
          segmentName: getSegmentNameById(s.segment_id)
        }))

      const inUseDetails = (response?.data?.segments || [])
        .filter(s => s.in_use)
        .map(s => ({
          segmentId: s.segment_id,
          segmentName: getSegmentNameById(s.segment_id),
          campaignNames: (s.usage || []).map(u => u.ref_id),
          campaignCount: (s.usage || []).length
        }))

      // An unknown/failed id is not the same as "not in use" - treat it as blocked
      // rather than silently letting it through as safe to delete.
      const failedDetails = (response?.data?.failed || []).map(f => ({
        segmentId: f.segment_id,
        segmentName: getSegmentNameById(f.segment_id),
        campaignNames: [f.reason || 'Segment not found'],
        campaignCount: 0
      }))

      const inUse = [...inUseDetails, ...failedDetails]

      return {
        safeToDelete,
        inUse,
        hasInUse: inUse.length > 0
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error'
      showError(`Failed to check segment usage: ${errorMsg}`)
      // Return empty result (fail closed - don't allow deletion if we can't verify)
      return { safeToDelete: [], inUse: [], hasInUse: true }
    }
  }

  // Bulk status update backed by PUT /segments/status (up to 100 ids in one call).
  // Only ever called with bulkAction 'delete' today, mapped to status 'inactive' to
  // match the single-segment delete flow (see deleteItem in the list page).
  const segmentBulkAction = async (selectedIDList, bulkAction, safeToDeleteIds = null) => {
    const validSegmentIds = safeToDeleteIds || (selectedIDList || []).map(id => String(id))

    if (validSegmentIds.length === 0) {
      showError('No segment IDs selected')
      return { deletedIds: [], failedIds: [] }
    }

    const status = bulkAction === 'delete' ? 'inactive' : bulkAction
    const response = await api.put(`${segmentsListUrl}/status`, {
      status,
      segment_ids: validSegmentIds
    })

    return {
      deletedIds: (response?.data?.updated || []).map(s => s.segment_id),
      failedIds: (response?.data?.failed || []).map(f => f.segment_id)
    }
  }

  const formatToDateString = dateString => {
    if (dateString === null) {
      return ''
    } else {
      const date = new Date(dateString)

      const year = date.getFullYear()

      const month = String(date.getMonth() + 1).padStart(2, '0')

      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    }
  }
  const resetSegmentState = () => {
    setSelectedSegment({})
    setSelectedSegmentID('')
    setPredefinedSegmentID('')
    setSegmentName('')
    setSegmentQueryPrompt('')
    setSegmentQuerySql('')
    setSegmentQueryJson(null)
    setQueryData('')
    setSegmentType('dynamic')
    setTags([])
    setExecuteQueryData('')
    setIsResponsePending(false)
  }

  const values = {
    tagListData,
    setTagListData,
    getTag: getTag,
    getSegmentList: getSegmentList,
    segmentListData,
    setSegmentListData,
    totalSegments,
    setTotalSegments,
    segmentSelectionData,
    setSegmentSelectionData,
    segmentName,
    setSegmentName,
    segmentQueryPrompt,
    setSegmentQueryPrompt,
    segmentQuerySql,
    setSegmentQuerySql,
    segmentQueryJson,
    setSegmentQueryJson,
    isResponsePending,
    setIsResponsePending,
    createSegment: createSegment,
    selectedSegment,
    setSelectedSegment,
    getSegmentData: getSegmentData,
    editSegment: editSegment,
    queryData,
    setQueryData,
    formatToDateString,
    getSegmentSelectlist: getSegmentSelectlist,
    setPredefinedSegmentID,
    predefinedSegmentID,
    executeQuery: executeQuery,
    executeQueryData,
    setExecuteQueryData,
    segmentType,
    setSegmentType,
    tags,
    setTags,
    refreshSegment: refreshSegment,
    segmentBulkAction,
    checkSegmentUsage: checkSegmentUsage,
    getSegmentUsage,
    resetSegmentState: resetSegmentState,
    getSegmentFilterList: getSegmentFilterList,
    segmentFilterList,
    setSegmentFilterList,
    segmentOtherFilterList,
    setSegmentOtherFilterList,
    hasTagFilter,
    setHasTagFilter,
    cloneSegment: cloneSegment,
    isExecuting,
    setIsExecuting,
    generateSegmentQuery: generateSegmentQuery,
    getSegmentWithCampaigns,
    segmentRegistryFields,
    getSegmentRegistry,
    segmentRuleError,
    setSegmentRuleError,
    runpodHealth,
    setRunpodHealth,
    isCheckingRunpodHealth,
    setIsCheckingRunpodHealth,
    runpodHealthError,
    setRunpodHealthError,
    checkRunpodHealth
  }

  return <SegmentContext.Provider value={values}>{children}</SegmentContext.Provider>
}

export { SegmentContext, SegmentProvider }
